export interface TFormField {
  id: string
  label: string
  type:
    | 'text'
    | 'email'
    | 'phone'
    | 'web_url'
    | 'number'
    | 'date'
    | 'boolean'
    | 'checkbox'
    | 'select'
  required: boolean
  placeholder?: string
  options?: string[] // For select, checkbox types
  min?: string | number // For number, date types
  max?: string | number // For number, date types
  step?: string | number // For number types
  pattern?: string // For text input validation
}

export interface TFormConfiguration {
  dbSchemaName?: string
  sendNotificationEmail?: boolean
  sendFeedbackEmail: boolean
  emailSubject: string
  replyToEmail: string
  emailTemplate: string
  customFields?: TFormField[]
  jsonSchema?: TTableSchema
}

export interface TSchemaProperty {
  type: string
  title: string
  description?: string
  format?: string
  readOnly?: boolean
  primaryKey?: boolean
  index?: boolean
}

export interface TTableSchema {
  $schema: string
  $id: string
  type: 'object'
  title: string
  description: string
  required: string[]
  properties: Record<string, TSchemaProperty>
}

// Form configurations with embedded schemas are stored in /src/shared/form-configs.json
const FORM_CONFIG_FILE_NAME = 'form-configs.json'

/**
 * This extracts the form schema ID from the target outer HTML
 * this field: data-schema-id="form-schema-id"
 * is specified in the backend, file:
 * src/core/coding_agent_router/prompts/gen2_prompts.py
 * search for this text:
 *  `id="your_schema"` // CRITICAL: Replace 'your_schema' with actual schema filename (without .json)
 * And then CustomForm should be the component from /heyboss-template repo.
 *
 * @param outerHtml - The outer HTML of the target
 * @returns
 */
export function extractFormSchemaIdFromTargetOuterHtml(
  outerHtml: string,
): string | null {
  const match = outerHtml.match(/data-schema-id="([^"]+)"/)
  return match ? match[1] : null
}

// Generic file read/write functions
function readFileFromTree(fileSystemTree: any, path: string[]): string | null {
  let current = fileSystemTree

  for (let i = 0; i < path.length - 1; i++) {
    current = current?.[path[i]]?.directory
    if (!current) return null
  }

  const fileName = path[path.length - 1]
  return current?.[fileName]?.file?.contents || null
}

function writeFileToTree(
  fileSystemTree: any,
  path: string[],
  contents: string,
): any {
  // Create a deep copy of the tree to avoid mutating the original
  const treeCopy = JSON.parse(JSON.stringify(fileSystemTree))

  const pathWithoutFile = path.slice(0, -1)
  const fileName = path[path.length - 1]

  // Ensure directory structure exists
  let current = treeCopy
  for (const segment of pathWithoutFile) {
    if (!current[segment]) {
      current[segment] = { directory: {} }
    }
    if (!current[segment].directory) {
      current[segment].directory = {}
    }
    current = current[segment].directory
  }

  // Write the file
  current[fileName] = {
    file: {
      contents,
    },
  }

  return treeCopy
}

// Email configuration operations
export function getFormConfiguration(
  fileSystemTree: any,
  schemaId?: string,
): TFormConfiguration | null {
  const contents = readFileFromTree(fileSystemTree, [
    'src',
    'shared',
    FORM_CONFIG_FILE_NAME,
  ])

  if (!contents) return null

  try {
    const allConfigs = JSON.parse(contents)

    // If schemaId is provided, return specific config
    if (schemaId) {
      return allConfigs[schemaId] || null
    }

    // For backward compatibility, if there's a top-level config, return it
    if (allConfigs.sendFeedbackEmail !== undefined) {
      return allConfigs
    }

    // Return the first config if no schemaId provided
    const firstKey = Object.keys(allConfigs)[0]
    return firstKey ? allConfigs[firstKey] : null
  } catch (error) {
    console.error('Error parsing form configuration:', error)
    return null
  }
}

export function getAllFormConfigurations(
  fileSystemTree: any,
): Record<string, TFormConfiguration> | null {
  const contents = readFileFromTree(fileSystemTree, [
    'src',
    'shared',
    FORM_CONFIG_FILE_NAME,
  ])

  if (!contents) return null

  try {
    const configs = JSON.parse(contents)

    // For each configuration, try to load the corresponding schema and extract table name
    for (const schemaId in configs) {
      const dbSchemaName = configs[schemaId].dbSchemaName
      const schemaContents = readFileFromTree(fileSystemTree, [
        'src',
        'shared',
        'schemas',
        `${dbSchemaName}.json`,
      ])
      if (schemaContents) {
        try {
          const schema = JSON.parse(schemaContents)
          // Extract table name from $id field (e.g., "urn:table:survey_submissions" -> "survey_submissions")
          if (schema.$id && typeof schema.$id === 'string') {
            const match = schema.$id.match(/urn:table:(.+)/)
            if (match?.[1]) {
              configs[schemaId].dbSchemaName = match[1]
            }
          }
        } catch (e) {
          console.error(`Error parsing schema for ${schemaId}:`, e)
        }
      }
    }

    return configs
  } catch (error) {
    console.error('Error parsing form configurations:', error)
    return null
  }
}

export function getDefaultFormConfiguration(): TFormConfiguration {
  return {
    sendNotificationEmail: false,
    sendFeedbackEmail: false,
    emailSubject: 'New Form Submission',
    replyToEmail: '',
    emailTemplate:
      '<p>Hello,</p><p>You have received a new form submission:</p><p>{{FORM_DATA}}</p><p>Best regards</p>',
    customFields: [],
  }
}

export function updateFormConfiguration(
  fileSystemTree: any,
  schemaId: string,
  updates: Partial<TFormConfiguration>,
): any {
  // Get all current configurations
  const allConfigs = getAllFormConfigurations(fileSystemTree) || {}

  // Get current configuration for this schema or use default
  const currentConfig = allConfigs[schemaId] || getDefaultFormConfiguration()

  // Merge updates
  const mergedConfig = { ...currentConfig, ...updates }

  // If we have a jsonSchema, remove customFields to avoid confusion
  if (mergedConfig.jsonSchema) {
    mergedConfig.customFields = undefined
  }

  allConfigs[schemaId] = mergedConfig

  // Write back all configurations
  return writeFileToTree(
    fileSystemTree,
    ['src', 'shared', FORM_CONFIG_FILE_NAME],
    JSON.stringify(allConfigs, null, 2),
  )
}

export function addFormField(
  fileSystemTree: any,
  schemaId: string,
  field: Omit<TFormField, 'id'>,
): any {
  const allConfigs = getAllFormConfigurations(fileSystemTree) || {}
  const currentConfig = allConfigs[schemaId] || getDefaultFormConfiguration()

  // If there's a jsonSchema, add to its properties
  if (currentConfig.jsonSchema) {
    // Generate a field name from the label
    const baseFieldName =
      field.label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '') || 'field'

    // Ensure uniqueness by checking existing properties
    let fieldName = baseFieldName
    let counter = 1
    while (currentConfig.jsonSchema.properties[fieldName]) {
      fieldName = `${baseFieldName}_${counter}`
      counter++
    }

    // Create the schema property based on field type
    const schemaProperty: TSchemaProperty = {
      type: mapFormTypeToSchemaType(field.type),
      title: field.label,
      ...(field.placeholder && { description: field.placeholder }),
    }

    // Handle special field types and formats
    if (field.type === 'email') {
      schemaProperty.format = 'email'
    } else if (field.type === 'date') {
      schemaProperty.format = 'date'
    } else if (field.type === 'phone') {
      schemaProperty.format = 'tel'
      // Add pattern for phone validation
      ;(schemaProperty as any).pattern = '^[\\+]?[1-9][\\d]{0,15}$'
    } else if (field.type === 'web_url') {
      schemaProperty.format = 'uri'
      // Strict pattern: either valid domain or complete https URL
      ;(schemaProperty as any).pattern =
        '^(https://[a-zA-Z0-9]([a-zA-Z0-9.-]{0,61}[a-zA-Z0-9])?\\.[a-zA-Z0-9]([a-zA-Z0-9.-]{0,61}[a-zA-Z0-9])?|(www\\.)?[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)$'
    } else if (field.type === 'select') {
      ;(schemaProperty as any).enum = field.options || []
    }

    // Add min/max for number types
    if (field.type === 'number') {
      if (field.min !== undefined) (schemaProperty as any).minimum = field.min
      if (field.max !== undefined) (schemaProperty as any).maximum = field.max
      if (field.step !== undefined)
        (schemaProperty as any).multipleOf = field.step
    }

    // Add pattern for validation
    if (field.pattern) {
      ;(schemaProperty as any).pattern = field.pattern
    }

    // Update the schema - ensure new field is added at the end
    // We need to preserve the order of existing properties and add the new one last
    const updatedProperties = { ...currentConfig.jsonSchema.properties }

    // Add the new property at the end
    updatedProperties[fieldName] = schemaProperty

    const updatedSchema = {
      ...currentConfig.jsonSchema,
      properties: updatedProperties,
      required: field.required
        ? [...(currentConfig.jsonSchema.required || []), fieldName]
        : currentConfig.jsonSchema.required || [],
    }

    return updateFormConfiguration(fileSystemTree, schemaId, {
      jsonSchema: updatedSchema,
    })
  }
  // Fallback to customFields if no schema exists
  const newField: TFormField = {
    ...field,
    id: `field_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
  }

  return updateFormConfiguration(fileSystemTree, schemaId, {
    customFields: [...(currentConfig.customFields || []), newField],
  })
}

export function updateFormField(
  fileSystemTree: any,
  schemaId: string,
  fieldId: string,
  updates: Partial<Omit<TFormField, 'id'>>,
): any {
  const allConfigs = getAllFormConfigurations(fileSystemTree) || {}
  const currentConfig = allConfigs[schemaId] || getDefaultFormConfiguration()

  // If there's a jsonSchema, update its properties
  if (currentConfig.jsonSchema) {
    // Extract the field name from the fieldId (format: field_<name>)
    const fieldName = fieldId.startsWith('field_')
      ? fieldId.substring(6)
      : fieldId

    if (!currentConfig.jsonSchema.properties[fieldName]) {
      return fileSystemTree // Field not found
    }

    // Create the updated schema property
    const updatedProperty: TSchemaProperty = {
      ...currentConfig.jsonSchema.properties[fieldName],
      ...(updates.label && { title: updates.label }),
      ...(updates.placeholder && { description: updates.placeholder }),
    }

    // Handle special field types
    if (updates.type) {
      updatedProperty.type = mapFormTypeToSchemaType(updates.type)

      // Set appropriate format based on type
      if (updates.type === 'email') {
        updatedProperty.format = 'email'
      } else if (updates.type === 'date') {
        updatedProperty.format = 'date'
      } else {
        updatedProperty.format = undefined
      }

      // Handle enum for select/checkbox
      if (updates.type === 'select' || updates.type === 'checkbox') {
        ;(updatedProperty as any).enum = updates.options || []
      } else {
        ;(updatedProperty as any).enum = undefined
      }

      // Handle number constraints
      if (updates.type === 'number') {
        if (updates.min !== undefined)
          (updatedProperty as any).minimum = updates.min
        if (updates.max !== undefined)
          (updatedProperty as any).maximum = updates.max
        if (updates.step !== undefined)
          (updatedProperty as any).multipleOf = updates.step
      } else {
        ;(updatedProperty as any).minimum = undefined
        ;(updatedProperty as any).maximum = undefined
        ;(updatedProperty as any).multipleOf = undefined
      }
    } else if (updates.options !== undefined) {
      ;(updatedProperty as any).enum = updates.options
    }

    // Update pattern if provided
    if (updates.pattern !== undefined) {
      ;(updatedProperty as any).pattern = updates.pattern || undefined
    }

    // Handle required field update
    let newRequired = currentConfig.jsonSchema.required || []
    if (updates.required !== undefined) {
      if (updates.required && !newRequired.includes(fieldName)) {
        newRequired = [...newRequired, fieldName]
      } else if (!updates.required) {
        newRequired = newRequired.filter((name) => name !== fieldName)
      }
    }

    // Update the schema
    const updatedSchema = {
      ...currentConfig.jsonSchema,
      properties: {
        ...currentConfig.jsonSchema.properties,
        [fieldName]: updatedProperty,
      },
      required: newRequired,
    }

    return updateFormConfiguration(fileSystemTree, schemaId, {
      jsonSchema: updatedSchema,
    })
  }
  // Fallback to customFields if no schema exists
  const updatedFields = (currentConfig.customFields || []).map((field) =>
    field.id === fieldId ? { ...field, ...updates } : field,
  )

  return updateFormConfiguration(fileSystemTree, schemaId, {
    customFields: updatedFields,
  })
}

export function removeFormField(
  fileSystemTree: any,
  schemaId: string,
  fieldId: string,
): any {
  const allConfigs = getAllFormConfigurations(fileSystemTree) || {}
  const currentConfig = allConfigs[schemaId] || getDefaultFormConfiguration()

  // If there's a jsonSchema, remove from its properties
  if (currentConfig.jsonSchema) {
    // Extract the field name from the fieldId (format: field_<name>)
    const fieldName = fieldId.startsWith('field_')
      ? fieldId.substring(6)
      : fieldId

    // Create a copy of properties without the field
    const { [fieldName]: _, ...remainingProperties } =
      currentConfig.jsonSchema.properties

    // Remove from required array if present
    const newRequired = (currentConfig.jsonSchema.required || []).filter(
      (name) => name !== fieldName,
    )

    // Update the schema
    const updatedSchema = {
      ...currentConfig.jsonSchema,
      properties: remainingProperties,
      required: newRequired,
    }

    return updateFormConfiguration(fileSystemTree, schemaId, {
      jsonSchema: updatedSchema,
    })
  }
  // Fallback to customFields if no schema exists
  const filteredFields = (currentConfig.customFields || []).filter(
    (field) => field.id !== fieldId,
  )

  return updateFormConfiguration(fileSystemTree, schemaId, {
    customFields: filteredFields,
  })
}

function mapSchemaTypeToFormType(
  type: string,
  format?: string,
  enumValues?: any[],
): TFormField['type'] {
  // Check format first for specific types
  if (format === 'email') return 'email'
  if (format === 'date') return 'date'
  if (format === 'tel') return 'phone'
  if (format === 'uri') return 'web_url'

  // Check type
  if (type === 'boolean') return 'boolean'
  if (type === 'integer' || type === 'number') return 'number'

  // If it has enum values, default to select
  if (enumValues && enumValues.length > 0) return 'select'

  // Default to text for strings and unknown types
  return 'text'
}

function mapFormTypeToSchemaType(formType: TFormField['type']): string {
  if (formType === 'boolean' || formType === 'checkbox') return 'boolean'
  if (formType === 'number') return 'number'
  // All other types map to string
  return 'string'
}

// Helper to convert form configuration to Formily schema
export function generateFormilySchemaFromFormConfig(
  formConfig: TFormConfiguration,
): any {
  if (!formConfig.customFields || formConfig.customFields.length === 0) {
    return { type: 'object', properties: {} }
  }

  const properties: Record<string, any> = {}

  for (const field of formConfig.customFields) {
    const fieldKey = field.id.replace('field_', '')

    // Map form field type to Formily component
    let component = 'Input'
    let componentProps: any = {
      placeholder: field.placeholder,
    }

    switch (field.type) {
      case 'text':
        component = 'Input'
        break
      case 'email':
        component = 'Input'
        componentProps.type = 'email'
        break
      case 'phone':
        component = 'Input'
        componentProps.type = 'tel'
        break
      case 'web_url':
        component = 'Input'
        componentProps.type = 'url'
        break
      case 'number':
        component = 'InputNumber'
        if (field.min !== undefined) componentProps.min = field.min
        if (field.max !== undefined) componentProps.max = field.max
        if (field.step !== undefined) componentProps.step = field.step
        break
      case 'date':
        component = 'DateTimePicker'
        break
      case 'boolean':
        component = 'Switch'
        break
      case 'checkbox':
        component = 'Checkbox'
        break
      case 'select':
        component = 'Select'
        if (field.options) {
          componentProps.options = field.options.map((opt) => ({
            label: opt,
            value: opt,
          }))
        }
        break
    }

    properties[fieldKey] = {
      type:
        field.type === 'boolean'
          ? 'boolean'
          : field.type === 'number'
            ? 'number'
            : 'string',
      title: field.label,
      'x-decorator': 'FormItem',
      'x-component': component,
      'x-component-props': componentProps,
      'x-validator': field.required
        ? [{ required: true, message: `${field.label} is required` }]
        : [],
    }

    // Add enum for select fields
    if (field.type === 'select' && field.options) {
      properties[fieldKey].enum = field.options.map((opt) => ({
        label: opt,
        value: opt,
      }))
    }
  }

  return {
    type: 'object',
    properties,
  }
}

// Helper to extract form fields from JSON schema
export function extractFormFieldsFromSchema(
  schema: TTableSchema | undefined,
): TFormField[] {
  if (!schema || !schema.properties) return []

  // Exclude system fields
  const systemFields = ['id', 'created_at', 'updated_at']

  // Use Object.keys to preserve insertion order, then filter
  return Object.keys(schema.properties)
    .filter((key) => !systemFields.includes(key))
    .map((key) => {
      const prop = schema.properties[key]
      const propWithEnum = prop as any

      // Map schema type to form field type
      const fieldType: TFormField['type'] = mapSchemaTypeToFormType(
        prop.type,
        prop.format,
        propWithEnum.enum,
      )

      // Build the form field object
      const field: TFormField = {
        id: `field_${key}`,
        label: prop.title || key,
        type: fieldType,
        required: schema.required?.includes(key) || false,
        placeholder: prop.description,
      }

      // Add optional properties
      if (propWithEnum.enum) {
        field.options = propWithEnum.enum
      }
      if (propWithEnum.minimum !== undefined) {
        field.min = propWithEnum.minimum
      }
      if (propWithEnum.maximum !== undefined) {
        field.max = propWithEnum.maximum
      }
      if (propWithEnum.multipleOf !== undefined) {
        field.step = propWithEnum.multipleOf
      }
      if (propWithEnum.pattern) {
        field.pattern = propWithEnum.pattern
      }

      return field
    })
}
